/**
 * Token Card Component
 * Displays an access token with its details and actions
 */

import React, { useState } from 'react';
import {
  AccessToken,
  TokenStatus,
  TokenStatusLabels,
  TokenStatusColors
} from '../../types/partnerAccess';

interface TokenCardProps {
  token: AccessToken;
  onRevoke: (tokenId: number) => Promise<void>;
  onRefresh: () => Promise<void>;
}

const TokenCard: React.FC<TokenCardProps> = ({ token, onRevoke, onRefresh }) => {
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  const handleRevoke = async () => {
    setIsRevoking(true);
    try {
      await onRevoke(token.tokenId);
      setShowRevokeDialog(false);
    } catch (err) {
      console.error('Error revoking token:', err);
    } finally {
      setIsRevoking(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('de-DE');
  };

  const isExpired = token.expiresAt && new Date(token.expiresAt) < new Date();
  const isActive = token.status === TokenStatus.Active && !isExpired;

  return (
    <div style={{
      ...styles.card,
      borderLeft: `4px solid ${TokenStatusColors[token.status]}`
    }}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h3 style={styles.name}>{token.tokenName}</h3>
          <p style={styles.partner}>{token.partnerName}</p>
        </div>
        <div style={{
          ...styles.statusBadge,
          backgroundColor: TokenStatusColors[token.status]
        }}>
          {TokenStatusLabels[token.status]}
        </div>
      </div>

      {/* Description */}
      {token.description && (
        <p style={styles.description}>{token.description}</p>
      )}

      {/* Details */}
      <div style={styles.details}>
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>Token-ID:</span>
          <span style={styles.detailValue}>#{token.tokenId}</span>
        </div>
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>Erstellt:</span>
          <span style={styles.detailValue}>{formatDate(token.createdAt)}</span>
        </div>
        {token.expiresAt && (
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Läuft ab:</span>
            <span style={{
              ...styles.detailValue,
              color: isExpired ? '#dc3545' : '#495057'
            }}>
              {formatDate(token.expiresAt)}
            </span>
          </div>
        )}
        {token.lastUsedAt && (
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Zuletzt verwendet:</span>
            <span style={styles.detailValue}>{formatDate(token.lastUsedAt)}</span>
          </div>
        )}
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>API-Aufrufe:</span>
          <span style={styles.detailValue}>{token.usageCount}</span>
        </div>
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>Rate Limit:</span>
          <span style={styles.detailValue}>{token.rateLimitPerHour}/h</span>
        </div>
        {token.allowedIpAddresses && (
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>IP-Whitelist:</span>
            <span style={styles.detailValue}>{token.allowedIpAddresses}</span>
          </div>
        )}
      </div>

      {/* Revocation Info */}
      {token.status === TokenStatus.Revoked && token.revokedAt && (
        <div style={styles.revocationInfo}>
          <strong>Widerrufen:</strong> {formatDate(token.revokedAt)}
          {token.revocationReason && (
            <div><strong>Grund:</strong> {token.revocationReason}</div>
          )}
        </div>
      )}

      {/* Actions */}
      {isActive && (
        <div style={styles.actions}>
          <button
            onClick={() => setShowRevokeDialog(true)}
            style={styles.revokeButton}
          >
            Widerrufen
          </button>
        </div>
      )}

      {/* Revoke Dialog */}
      {showRevokeDialog && (
        <div style={styles.dialogOverlay} onClick={() => setShowRevokeDialog(false)}>
          <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.dialogTitle}>Token widerrufen</h3>
            <p style={styles.dialogText}>
              Möchten Sie den Token "{token.tokenName}" wirklich widerrufen?
            </p>
            <div style={styles.dialogActions}>
              <button
                onClick={() => setShowRevokeDialog(false)}
                style={styles.cancelButton}
                disabled={isRevoking}
              >
                Abbrechen
              </button>
              <button
                onClick={handleRevoke}
                style={styles.confirmButton}
                disabled={isRevoking}
              >
                {isRevoking ? 'Widerrufe...' : 'Widerrufen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'box-shadow 0.2s',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1rem',
  },
  name: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#2c3e50',
    margin: 0,
  },
  partner: {
    fontSize: '0.9rem',
    color: '#6c757d',
    margin: '0.25rem 0 0 0',
  },
  statusBadge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
    color: 'white',
    fontSize: '0.85rem',
    fontWeight: '600',
  },
  description: {
    fontSize: '0.95rem',
    color: '#495057',
    marginBottom: '1rem',
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.9rem',
  },
  detailLabel: {
    color: '#6c757d',
    fontWeight: '500',
  },
  detailValue: {
    color: '#495057',
    fontWeight: '600',
  },
  revocationInfo: {
    padding: '0.75rem',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    fontSize: '0.9rem',
    color: '#721c24',
    marginBottom: '1rem',
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
    paddingTop: '1rem',
    borderTop: '1px solid #e9ecef',
  },
  revokeButton: {
    flex: 1,
    padding: '0.5rem',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  dialogOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  dialog: {
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '12px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  dialogTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: '1rem',
  },
  dialogText: {
    fontSize: '1rem',
    color: '#495057',
    marginBottom: '1rem',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ced4da',
    borderRadius: '8px',
    fontSize: '1rem',
    fontFamily: 'inherit',
    resize: 'vertical',
    marginBottom: '1rem',
  },
  dialogActions: {
    display: 'flex',
    gap: '0.5rem',
  },
  cancelButton: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  confirmButton: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default TokenCard;

